import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart, GraduationCap, Loader2, Check } from "lucide-react";
import { PROGRAMS } from "@/data/programs";
import Faq from "@/components/Faq";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function VolunteerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interests: "", availability: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/volunteers", form);
      setDone(true);
      toast.success("Thank you for volunteering! We'll reach out.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  if (done)
    return (
      <div data-testid="volunteer-success" className="py-12 text-center">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
        <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">You're on the list!</h3>
        <p className="mt-2 text-slate-600">Thank you for stepping up to inspire the next generation.</p>
      </div>
    );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="volunteer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name *"><input data-testid="volunteer-name" required value={form.name} onChange={update("name")} className={inputCls} /></Field>
        <Field label="Email *"><input data-testid="volunteer-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <Field label="Phone"><input data-testid="volunteer-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
        <Field label="Availability"><input data-testid="volunteer-availability" value={form.availability} onChange={update("availability")} placeholder="e.g. Weekends" className={inputCls} /></Field>
      </div>
      <Field label="What would you like to help with?"><input data-testid="volunteer-interests" value={form.interests} onChange={update("interests")} placeholder="Mentoring, events, instruction…" className={inputCls} /></Field>
      <Field label="Anything else?"><textarea data-testid="volunteer-message" rows={3} value={form.message} onChange={update("message")} className={inputCls} /></Field>
      <button data-testid="volunteer-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brandnavy px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <HandHeart className="h-5 w-5" />} Become a Volunteer
      </button>
    </form>
  );
}

function ProgramInquiryForm() {
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", child_age: "", program: PROGRAMS[0].name, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/program-inquiries", form);
      setDone(true);
      toast.success("Inquiry sent! We'll be in touch about enrollment.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  if (done)
    return (
      <div data-testid="program-inquiry-success" className="py-12 text-center">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
        <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Inquiry received!</h3>
        <p className="mt-2 text-slate-600">We'll reach out with enrollment details for your child.</p>
      </div>
    );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="program-inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Parent / Guardian Name *"><input data-testid="inquiry-parent-name" required value={form.parent_name} onChange={update("parent_name")} className={inputCls} /></Field>
        <Field label="Email *"><input data-testid="inquiry-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <Field label="Phone"><input data-testid="inquiry-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
        <Field label="Child's Age"><input data-testid="inquiry-child-age" value={form.child_age} onChange={update("child_age")} className={inputCls} /></Field>
      </div>
      <Field label="Program of Interest *">
        <select data-testid="inquiry-program" value={form.program} onChange={update("program")} className={inputCls}>
          {PROGRAMS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
          <option value="Not sure / Multiple">Not sure / Multiple</option>
        </select>
      </Field>
      <Field label="Questions or notes"><textarea data-testid="inquiry-message" rows={3} value={form.message} onChange={update("message")} className={inputCls} /></Field>
      <button data-testid="inquiry-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-orange-400 disabled:opacity-60">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <GraduationCap className="h-5 w-5" />} Request Enrollment Info
      </button>
    </form>
  );
}

export default function GetInvolved() {
  return (
    <div data-testid="get-involved-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Get Involved</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Join the movement.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Whether you're a parent, volunteer, or supporter — there's a place for you at LP Foundation. Help us inspire the
            next generation, or enroll your child in a program built to help them perform.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-8">
        <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg">
          <div className="flex items-center gap-2 text-accent">
            <HandHeart className="h-5 w-5" />
            <h2 className="font-display text-xl font-bold text-brandnavy">Volunteer Interest</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Share your time and talents to help inspire youth.</p>
          <div className="mt-6"><VolunteerForm /></div>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg">
          <div className="flex items-center gap-2 text-accent">
            <GraduationCap className="h-5 w-5" />
            <h2 className="font-display text-xl font-bold text-brandnavy">Enroll Your Child</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Tell us about your child and the program you're interested in.</p>
          <div className="mt-6"><ProgramInquiryForm /></div>
        </div>
      </section>

      <Faq />
    </div>
  );
}
